import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { C, font, inp, lbl, btnS } from "../../../../lib/styles.js";
import { normalizeCrew, mkUuid } from "../../../../lib/utils.js";
import { CAPTURE_LOG_STATUSES, CAPTURE_LOG_STATUS_DEFAULT, EQUIP_CHIP_COLORS, EQUIP_CHIP_DEFAULT } from "../../../../lib/constants.js";
import { useData } from "../../../../context/DataContext.jsx";
import { stampDeleted, relativeTime } from "../../lib/captureBin.js";
// Segment 3 — read-only handheld/equipment subjects merged into the same list.
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { resolvePhotoUrl } from "../../../../lib/handheldStorage.js";
import { pad3 } from "../../../../lib/handheldNaming.js";
// Slice 3.2: a rig capture's "Process" action (start work from anywhere, a phone included).
import ProcessCaptureSheet, { processableSn } from "./ProcessCaptureSheet.jsx";

/* ═══════════════════════════════════════════════════════════════
   CaptureLog — capture-day data entry view
   Props: { job, editing, isCrew, canEditShootWork, canManageJobs,
            currentSessionId, currentUserLabel, onRestoreRow, updateJobFields }
   Capture-day fields (captureLog rows, peoplePresent, clientContacts,
   captureNotes, issues, actualArrival, actualWrap, travelTime) can be
   written through an injected update path for multi-day session scoping.

   Editability: crew + admin can add/edit/delete (canEditShootWork). Crew are
   always in edit (no edit toggle), admins edit when the toggle is on — matches
   JobPrepTab. Viewers are read-only. Delete is a SOFT delete: rows move into a
   job-level `captureLogDeleted[]` bin ("Recently Deleted"); anyone-who-can-edit
   can restore; only admins (canManageJobs) can permanently empty the bin.
═══════════════════════════════════════════════════════════════ */

/* ═══ Helpers ═══ */

const MAX_PHOTO_BYTES = 25 * 1024 * 1024; // 25 MB

// v4 display precedence — displayName is the source of truth from v4 forward;
// `subject` remains as a legacy fallback so existing rows render unchanged.
export const subjectDisplay = (row) =>
  row?.displayName ?? row?.subject ?? row?.subjectName ?? "";

// Lowercase fallback covers hand-edited/legacy imports; v4 functions write captureAttemptId.
const isIngestCaptureRow = (row) => !!(row?.captureAttemptId || row?.captureattemptid);

// Shared equipment chip color (same palette Processing/Delivery use).
const getEquipColor = (name) => EQUIP_CHIP_COLORS[name] || EQUIP_CHIP_DEFAULT;

const UndoIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M3 7v6h6" />
    <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13" />
  </svg>
);

/* ═══ Static styles (hoisted — depend only on module-level C/font tokens) ═══ */
const GRID_COLS_EDIT = "32px 48px minmax(160px,1.4fr) minmax(140px,1.2fr) 100px 110px 110px minmax(160px,1.4fr) 72px";
const GRID_COLS_READ = "32px 48px minmax(160px,1.4fr) minmax(140px,1.2fr) 100px 110px 110px minmax(160px,1.4fr)";

const cellInpStyle = {
  width: "100%", background: "transparent", border: "1px solid transparent",
  borderRadius: 4, padding: "6px 0", fontSize: 12.5, fontFamily: font,
  fontWeight: 300, color: C.text, outline: "none",
  transition: "background 0.1s, border-color 0.1s",
  boxSizing: "border-box",
};
const cellSelectStyle = {
  ...cellInpStyle,
  cursor: "pointer", paddingRight: 22,
  appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 16 16' fill='none' stroke='%236B6B6B' stroke-width='1.5'><polyline points='4,6 8,10 12,6'/></svg>")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 6px center",
};
const cellTextStyle = {
  fontSize: 12.5, fontFamily: font, fontWeight: 300, color: C.text,
  padding: "6px 18px 6px 10px",
};
const cellMonoStyle = { ...cellTextStyle, fontFamily: "'DM Mono', 'SF Mono', monospace", fontSize: 12 };
const cellEmptyStyle = { ...cellTextStyle, color: C.muted, opacity: 0.55, fontStyle: "italic" };

/* ═══ Equipment checklist cell (limited to the job's selected gear) ═══ */
const equipTriggerStyle = {
  ...cellInpStyle,
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
  cursor: "pointer", textAlign: "left",
};
const equipMenuStyle = {
  position: "fixed", zIndex: 50,
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "4px 0",
  maxHeight: 240, overflowY: "auto", fontFamily: font, fontWeight: 300,
};
const equipOptionStyle = {
  display: "flex", alignItems: "center", gap: 8, padding: "6px 12px",
  fontSize: 12, color: C.text, cursor: "pointer", whiteSpace: "nowrap",
};

const headerCellStyle = {
  padding: "10px 10px 8px",
  fontSize: 9, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.14em",
  color: C.muted, fontFamily: font,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${C.border}`,
  minHeight: 52,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  gap: 4,
};
const rowCellStyle = {
  padding: "6px 10px",
  borderBottom: `1px solid ${C.border}`,
  display: "flex", alignItems: "center",
  minHeight: 48,
};
const lastRowCellStyle = { ...rowCellStyle, borderBottom: "none" };

const applyLinkStyle = {
  display: "block", marginTop: 0, fontSize: 9, color: C.blue,
  background: "transparent", border: "none", padding: 0, cursor: "pointer",
  fontFamily: font, fontWeight: 400,
  height: 14,
  lineHeight: "14px",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
};

const protectedDeleteConfirmStyle = {
  border: `1px solid ${C.danger}`,
  background: C.danger,
  color: "#fff",
  borderRadius: 4,
  padding: "2px 5px",
  fontSize: 8.5,
  fontFamily: font,
  cursor: "pointer",
};

const protectedDeleteCancelStyle = {
  border: `1px solid ${C.border}`,
  background: "transparent",
  color: C.muted,
  borderRadius: 4,
  padding: "2px 5px",
  fontSize: 8.5,
  fontFamily: font,
  cursor: "pointer",
};

// Slice 3.2: a rig capture's "Process" button (opens ProcessCaptureSheet). 32 px tall in the
// desktop grid; the phone's stacked card gives it the row's width.
const processBtnStyle = {
  marginTop: 6,
  minHeight: 32,
  padding: "0 12px",
  border: `1px solid ${C.border}`,
  background: C.surface,
  color: C.text,
  borderRadius: 4,
  fontSize: 12,
  fontFamily: font,
  cursor: "pointer",
};

/* ═══ Recently Deleted bin styles ═══ */
const binBtnStyle = {
  ...btnS, padding: "5px 12px", fontSize: 10,
  display: "flex", alignItems: "center", gap: 5,
  color: C.muted, borderColor: C.border, borderStyle: "dashed",
};
const binCountStyle = {
  fontFamily: "'DM Mono', 'SF Mono', monospace", fontSize: 9.5,
  color: "#fff", background: C.amber, borderRadius: 999,
  padding: "0 6px", lineHeight: "15px",
};
const popStyle = {
  position: "absolute", top: "100%", right: 0, marginTop: 7, width: 430, maxWidth: "88vw",
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
  boxShadow: "0 10px 30px rgba(0,0,0,0.12)", zIndex: 30, overflow: "hidden",
  fontFamily: font, fontWeight: 300, textAlign: "left",
};
const popHeadStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
  padding: "11px 13px", borderBottom: `1px solid ${C.border}`,
};
const popTitleStyle = {
  fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted, fontWeight: 500,
};
const popXStyle = { background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 15, lineHeight: 1 };
const binRowStyle = {
  display: "grid", gridTemplateColumns: "1fr 92px", alignItems: "center", gap: 10,
  padding: "9px 13px", borderBottom: `1px solid ${C.border}`, fontSize: 12,
};
const binWhoStyle = { fontSize: 10.5, color: C.muted, marginTop: 2 };
const rigTagStyle = {
  fontFamily: "'DM Mono', 'SF Mono', monospace", fontSize: 8.5, letterSpacing: "0.1em", color: C.blue,
  border: "1px solid rgba(59,130,246,0.35)", background: "rgba(59,130,246,0.08)", borderRadius: 3, padding: "1px 5px",
};
const restoreStyle = {
  fontSize: 10.5, padding: "4px 11px", borderRadius: 6, border: `1px solid ${C.border}`,
  background: C.surface, color: C.text, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 5, justifySelf: "end",
  fontFamily: font,
};
const popFootStyle = {
  padding: "10px 13px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
  background: C.surfaceAlt, borderTop: `1px solid ${C.border}`,
};
const popNoteStyle = { fontSize: 10.5, color: C.muted, maxWidth: 230 };
const emptyBtnStyle = {
  fontSize: 10.5, padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.4)",
  background: C.surface, color: C.danger, cursor: "pointer", whiteSpace: "nowrap", fontFamily: font,
};
const emptyBtnLockedStyle = { color: C.muted, borderColor: C.border, cursor: "not-allowed", opacity: 0.75 };
const popEmptyStyle = { padding: "18px 14px", fontSize: 12, color: C.muted, textAlign: "center" };
const toastStyle = {
  position: "absolute", top: "100%", right: 0, marginTop: 7, zIndex: 25,
  background: "#1A1A1A", color: "#fff", borderRadius: 7, padding: "8px 12px", fontSize: 11.5,
  display: "flex", alignItems: "center", gap: 12, boxShadow: "0 8px 20px rgba(0,0,0,0.18)", whiteSpace: "nowrap",
};
const toastLinkStyle = {
  color: "#7db0ff", cursor: "pointer", background: "transparent", border: "none",
  fontWeight: 500, fontFamily: font, fontSize: 11.5, padding: 0,
};

/* ═══ Segment 3 — handheld/equipment subjects as READ-ONLY rows in the same list ═══
   These rows come from jobs/{jobId}/subjects (the Equipment ingest line). They are
   strictly read-only here: never editable, never deletable, and NEVER added to
   job.captureLog or the rig counts. Visually grouped with a faint tint + an
   "Equipment" badge so the source is unambiguous; managed on the Equipment tab. */
const HH_ACCENT = "#7c5cd6"; // distinct from the rig blue
const hhCellStyle = { ...rowCellStyle, background: "rgba(124,92,214,0.045)" };
const hhLastCellStyle = { ...lastRowCellStyle, background: "rgba(124,92,214,0.045)" };
const equipmentBadgeStyle = {
  fontFamily: "'DM Mono', 'SF Mono', monospace", fontSize: 8.5, letterSpacing: "0.1em",
  textTransform: "uppercase", color: HH_ACCENT,
  border: "1px solid rgba(124,92,214,0.35)", background: "rgba(124,92,214,0.10)",
  borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap",
};
const hhGearChipStyle = {
  fontFamily: "'DM Mono', 'SF Mono', monospace", fontSize: 10, color: C.text,
  background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 4, padding: "1px 6px",
};

// Derive a concise read-only stage label from the subject's lifecycle data.
function handheldStage(s) {
  if (s.exports?.length) return { label: "exported", color: C.blue };
  if (s.projectIds?.length) return { label: "in project", color: C.blue };
  if (s.rawSets?.length) return { label: "raw", color: C.amber };
  return { label: "empty", color: C.muted };
}

const handheldThumbUrlCache = new Map();
function resolveHandheldThumb(path) {
  if (!path) return Promise.resolve(null);
  if (handheldThumbUrlCache.has(path)) return Promise.resolve(handheldThumbUrlCache.get(path));
  const pending = resolvePhotoUrl(path)
    .then((url) => {
      handheldThumbUrlCache.set(path, url);
      return url;
    })
    .catch((err) => {
      handheldThumbUrlCache.delete(path);
      throw err;
    });
  handheldThumbUrlCache.set(path, pending);
  return pending;
}

// Reference-photo thumbnail: resolves the stored Storage PATH to a URL at view
// time (mirrors handheldStorage.resolvePhotoUrl). Falls back to a muted box.
function HandheldThumb({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!path) { setUrl(null); return undefined; }
    resolveHandheldThumb(path).then((u) => { if (alive) setUrl(u); }).catch(() => { if (alive) setUrl(null); });
    return () => { alive = false; };
  }, [path]);
  const base = { width: 36, height: 36, borderRadius: 6, border: `1px solid ${C.border}` };
  if (url) return <div style={{ ...base, backgroundImage: `url("${url}")`, backgroundSize: "cover", backgroundPosition: "center" }} />;
  return <div style={{ ...base, background: C.surfaceAlt }} />;
}

// Read-only handheld rows, rendered INTO the same grid after the rig captureLog
// rows. Returns an array of display:contents rows so they align to the grid.
function HandheldCaptureRows({ subjects, canEdit }) {
  return subjects.map((s, idx) => {
    const isLast = idx === subjects.length - 1;
    const cell = isLast ? hhLastCellStyle : hhCellStyle;
    const stage = handheldStage(s);
    const gear = s.capturedBy ?? [];
    return (
      <div key={`hh_${s.id}`} className="cl-row cl-row-hh" style={{ display: "contents" }}>
        {/* # — the subject's own per-job number */}
        <div className="cl-cell" data-label="#" style={cell}>
          <span style={{ fontFamily: "'DM Mono', 'SF Mono', monospace", fontSize: 11, color: HH_ACCENT, opacity: 0.85 }}>
            {pad3(s.number)}
          </span>
        </div>
        {/* Photo */}
        <div className="cl-cell" data-label="Photo" style={cell}>
          <HandheldThumb path={s.referencePhoto} />
        </div>
        {/* Subject — name + Equipment source badge + category */}
        <div className="cl-cell" data-label="Subject" style={cell}>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12.5, fontFamily: font, fontWeight: 400, color: C.text }}>
                {s.displayName || "Untitled"}
              </span>
              <span style={equipmentBadgeStyle}>Equipment</span>
            </span>
            {s.category && <span style={{ fontSize: 10.5, color: C.muted }}>{s.category}</span>}
          </div>
        </div>
        {/* Equipment — the gear that captured it (subject.capturedBy) */}
        <div className="cl-cell" data-label="Equipment" style={cell}>
          {gear.length ? (
            <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {gear.map((g) => <span key={g} style={hhGearChipStyle}>{g}</span>)}
            </span>
          ) : <span style={cellEmptyStyle}>—</span>}
        </div>
        {/* Storage — n/a for handheld */}
        <div className="cl-cell" data-label="Storage" style={cell}><span style={cellEmptyStyle}>—</span></div>
        {/* Status — read-only stage label */}
        <div className="cl-cell" data-label="Status" style={cell}>
          <span style={{ fontSize: 11, fontFamily: font, color: stage.color }}>{stage.label}</span>
        </div>
        {/* Operator — n/a */}
        <div className="cl-cell" data-label="Operator" style={cell}><span style={cellEmptyStyle}>—</span></div>
        {/* Notes — points to where it's managed */}
        <div className="cl-cell" data-label="Notes" style={cell}><span style={cellEmptyStyle}>managed in Equipment</span></div>
        {/* Action — empty (read-only; never deletable) to keep grid alignment */}
        {canEdit && <div className="cl-cell" style={cell} />}
      </div>
    );
  });
}

// Resize image to ≤200px max dimension, JPEG 0.7 quality. Returns base64 data URL,
// or null on failure (corrupt file, oversized, or decode error).
function resizeToThumb(file, maxSize = 200) {
  return new Promise((resolve) => {
    if (file.size > MAX_PHOTO_BYTES) { resolve(null); return; }
    const reader = new FileReader();
    reader.onerror = () => { console.warn("FileReader failed", reader.error); resolve(null); };
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => { console.warn("Image decode failed"); resolve(null); };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > h) { if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; } }
        else { if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; } }
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function equipMapToText(rowEquip) {
  if (!rowEquip || typeof rowEquip !== "object") return "";
  return Object.keys(rowEquip).filter(k => rowEquip[k]).join(", ");
}

/* ═══ Photo cell ═══ */
function PhotoCell({ image, onSet, editing }) {
  const fileRef = useRef(null);
  const handleFile = useCallback((files) => {
    (async () => {
      const f = Array.from(files).find(x => x.type.startsWith("image/"));
      if (!f) return;
      if (f.size > MAX_PHOTO_BYTES) {
        alert(`Photo too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Max ${MAX_PHOTO_BYTES / 1024 / 1024} MB.`);
        return;
      }
      const src = await resizeToThumb(f);
      if (!src) {
        alert("Could not read this image. Please try a different file.");
        return;
      }
      onSet({ id: `img_${mkUuid()}`, src, name: f.name });
    })();
  }, [onSet]);

  if (image && image.src) {
    return (
      <div
        onClick={() => editing && fileRef.current?.click()}
        style={{
          width: 36, height: 36, borderRadius: 4, overflow: "hidden",
          background: C.surfaceAlt, cursor: editing ? "pointer" : "default",
        }}
      >
        <img src={image.src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        {editing && (
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(e) => { if (e.target.files.length > 0) handleFile(e.target.files); e.target.value = ""; }}
            style={{ display: "none" }}
          />
        )}
      </div>
    );
  }
  if (!editing) {
    return <div style={{ width: 36, height: 36, borderRadius: 4, background: C.surfaceAlt }} />;
  }
  return (
    <>
      <div
        onClick={() => fileRef.current?.click()}
        style={{
          width: 36, height: 36, borderRadius: 4,
          border: `1px dashed ${C.border}`, background: "transparent", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke={C.muted} strokeWidth="1.5">
          <path d="M8 3v10M3 8h10" />
        </svg>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={(e) => { if (e.target.files.length > 0) handleFile(e.target.files); e.target.value = ""; }}
        style={{ display: "none" }}
      />
    </>
  );
}

/* ═══ Status cell — badge in read, colored select in edit ═══ */
function StatusCell({ status, editing, onChange, testId }) {
  const isCaptured = status === "captured";
  const baseStyle = {
    color: isCaptured ? C.blue : C.muted,
    background: isCaptured ? "rgba(59,130,246,0.1)" : "rgba(107,107,107,0.1)",
    border: `1px solid ${isCaptured ? "rgba(59,130,246,0.3)" : "rgba(107,107,107,0.25)"}`,
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontFamily: font,
  };
  if (!editing) {
    return <span style={{ ...baseStyle, display: "inline-block", padding: "4px 10px" }}>{status || CAPTURE_LOG_STATUS_DEFAULT}</span>;
  }
  // Editable: a clean clickable pill. We intentionally do NOT use a custom inline-SVG
  // dropdown arrow here — Safari/WebKit mis-renders that data-URI background on a tiny
  // bold/uppercase styled <select>, producing stray glyph artifacts ("birds"). A plain
  // appearance:none pill renders identically to the read badge across browsers.
  return (
    <select
      data-testid={testId}
      value={status || CAPTURE_LOG_STATUS_DEFAULT}
      onChange={(e) => onChange(e.target.value)}
      style={{
        ...baseStyle,
        padding: "4px 10px",
        cursor: "pointer",
        outline: "none",
        WebkitAppearance: "none", MozAppearance: "none", appearance: "none",
        backgroundImage: "none",
      }}
    >
      {CAPTURE_LOG_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

/* ═══ Read-mode value display (used for actual times, capture notes, issues) ═══ */
function ReadValue({ value, padding = "8px 0" }) {
  // Presence check — `0` is a valid travelTime, so falsy check would hide it.
  const hasValue = value !== undefined && value !== null && value !== "";
  return (
    <div style={{
      fontSize: 12.5,
      color: hasValue ? C.text : C.muted,
      opacity: hasValue ? 1 : 0.5,
      padding,
      fontFamily: font,
      fontWeight: 300,
      lineHeight: 1.55,
    }}>
      {hasValue ? value : "—"}
    </div>
  );
}

/* ═══ Equipment cell — multi-pick checklist limited to the job's selected gear ═══ */
function EquipmentCell({ equipment, options = [], editing, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const eqMap = equipment || {};

  // Job gear first; then any names already set on this row but no longer in the job's
  // list (legacy free-text entries, or gear later removed from the job) so an existing
  // selection is never silently hidden — it stays visible and uncheckable-off-able.
  const names = useMemo(() => {
    const extras = Object.keys(eqMap).filter(k => eqMap[k] && !options.includes(k));
    return [...options, ...extras];
  }, [options, eqMap]);
  const summary = equipMapToText(eqMap);

  const openMenu = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) { setOpen(true); return; }
    const width = Math.max(r.width, 190);
    const estHeight = Math.min(240, Math.max(48, names.length * 32 + 8));
    const spaceBelow = window.innerHeight - r.bottom;
    // Flip above the trigger when there isn't room below and there's more room above.
    const openUp = spaceBelow < estHeight + 8 && r.top > spaceBelow;
    const top = openUp ? Math.max(8, r.top - estHeight - 4) : r.bottom + 4;
    // Clamp horizontally so the menu never spills off the right/left edge.
    const left = Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - width - 8));
    const maxHeight = openUp ? Math.min(240, r.top - 12) : Math.min(240, spaceBelow - 12);
    setPos({ left, top, width, maxHeight });
    setOpen(true);
  };

  // Fixed-positioned menu rendered in a portal so the spreadsheet's horizontal
  // scroll container can't clip it. Close on outside click, Esc, outer scroll, resize —
  // but NOT when scrolling inside the menu's own list.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (e.target.closest?.("[data-equip-menu]")) return;
      setOpen(false);
    };
    const onScroll = (e) => {
      if (e.target?.closest?.("[data-equip-menu]")) return; // scrolling the list itself is fine
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  if (!editing) {
    const chosen = names.filter(n => eqMap[n]);
    if (!chosen.length) return <span style={cellEmptyStyle}>—</span>;
    return (
      <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {chosen.map(n => {
          const color = getEquipColor(n);
          return (
            <span key={n} style={{ fontSize: 10.5, padding: "2px 7px", borderRadius: 4, background: color.bg, border: `1px solid ${color.border}`, color: color.text, whiteSpace: "nowrap" }}>
              {n}
            </span>
          );
        })}
      </span>
    );
  }

  const toggle = (name) => {
    const next = { ...eqMap };
    if (next[name]) delete next[name]; else next[name] = true;
    onChange(next);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="cl-sel"
        onClick={() => (open ? setOpen(false) : openMenu())}
        style={equipTriggerStyle}
        title={summary || "Select equipment"}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: summary ? C.text : C.muted }}>
          {summary || "Select…"}
        </span>
        <span style={{ color: C.muted, fontSize: 9, flexShrink: 0 }}>▾</span>
      </button>
      {open && pos && createPortal(
        <div data-equip-menu style={{ ...equipMenuStyle, left: pos.left, top: pos.top, minWidth: pos.width, maxHeight: pos.maxHeight ?? 240 }}>
          {names.length === 0 ? (
            <div style={{ ...equipOptionStyle, color: C.muted, fontStyle: "italic", cursor: "default" }}>
              No equipment configured
            </div>
          ) : (
            names.map((name) => (
              <label key={name} className="cl-equip-opt" style={equipOptionStyle}>
                <input
                  type="checkbox"
                  checked={!!eqMap[name]}
                  onChange={() => toggle(name)}
                  style={{ cursor: "pointer", accentColor: C.blue }}
                />
                {name}
              </label>
            ))
          )}
        </div>,
        document.body
      )}
    </>
  );
}

/* ═══ Header cell with optional Apply-all dropdown (Storage / Operator) ═══ */
function HeaderWithApplyAll({ label, options, applyLinkStyle, headerCellStyle, onApply, editing }) {
  return (
    <div style={headerCellStyle}>
      <span>{label}</span>
      {editing ? (
        <select
          value=""
          onChange={(e) => { if (e.target.value) onApply(e.target.value); }}
          style={applyLinkStyle}
        >
          <option value="">Apply all...</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <span style={{ height: 14 }} />
      )}
    </div>
  );
}

/* ═══ Editable contact list (Client Contacts / People Present) ═══ */
function ContactTable({ items, onUpdate, fields, label, addLabel, editing }) {
  const [hov, setHov] = useState(null);
  // Coerce legacy string entries to objects so nyc-suite-era data renders without React key warnings.
  const list = (items || []).map((r, i) => {
    if (typeof r === "string") return { id: `legacy_${i}_${r}`, name: r };
    return r;
  }).filter(r => r && typeof r === "object");

  const add = () => {
    const row = { id: `row_${mkUuid()}` };
    fields.forEach(f => row[f.key] = "");
    onUpdate([...list, row]);
  };
  const update = (id, key, val) => onUpdate(list.map(r => r.id === id ? { ...r, [key]: val } : r));
  const remove = (id) => onUpdate(list.filter(r => r.id !== id));

  if (!editing) {
    return (
      <div>
        <label style={lbl}>{label}</label>
        {list.length > 0 ? list.map(r => (
          <div key={r.id} style={{ fontSize: 12.5, color: C.text, fontFamily: font, fontWeight: 300, padding: "2px 0" }}>
            {fields.map(f => r[f.key]).filter(Boolean).join(" — ") || <span style={{ color: C.muted, opacity: 0.5 }}>—</span>}
          </div>
        )) : <div style={{ fontSize: 12.5, color: C.muted, opacity: 0.5, fontFamily: font, fontWeight: 300 }}>None</div>}
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <label style={{ ...lbl, marginBottom: 0 }}>{label}</label>
        <button onClick={add} style={{ ...btnS, padding: "4px 10px", fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M8 3v10M3 8h10" />
          </svg>
          {addLabel || "Add"}
        </button>
      </div>
      {list.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {list.map(r => (
            <div
              key={r.id}
              onMouseEnter={() => setHov(r.id)}
              onMouseLeave={() => setHov(null)}
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              {fields.map(f => (
                <input
                  key={f.key}
                  value={r[f.key] || ""}
                  onChange={(e) => update(r.id, f.key, e.target.value)}
                  placeholder={f.placeholder}
                  style={{ ...inp, fontSize: 12, padding: "7px 10px", flex: f.flex || 1 }}
                />
              ))}
              {hov === r.id && (
                <button
                  onClick={() => remove(r.id)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, opacity: 0.6, padding: 2, flexShrink: 0 }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {list.length === 0 && (
        <div style={{ fontSize: 12, color: C.muted, opacity: 0.5, fontFamily: font, fontWeight: 300 }}>None added</div>
      )}
    </div>
  );
}

/* ═══ Recently Deleted popover — job-level bin (one list across all days) ═══ */
function RecentlyDeletedPopover({ deletedRows, canManageJobs, confirmEmpty, onRestore, onEmpty, onClose, nowTs }) {
  return (
    <div style={popStyle} onClick={(e) => e.stopPropagation()}>
      <div style={popHeadStyle}>
        <span style={popTitleStyle}>Recently Deleted</span>
        <button style={popXStyle} onClick={onClose} title="Close">✕</button>
      </div>

      {deletedRows.length === 0 ? (
        <div style={popEmptyStyle}>Nothing deleted. Removed entries land here so you can pull them back.</div>
      ) : (
        deletedRows.map((d, i) => (
          <div key={d.id ?? i} style={i === deletedRows.length - 1 ? { ...binRowStyle, borderBottom: "none" } : binRowStyle}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ fontSize: 12, color: C.text }}>{subjectDisplay(d) || "No subject"}</span>
                {isIngestCaptureRow(d) && <span style={rigTagStyle}>RIG</span>}
              </div>
              <div style={binWhoStyle}>
                deleted by {d.deletedBy || "—"}{d.deletedAt ? ` · ${relativeTime(d.deletedAt, nowTs)}` : ""}
              </div>
            </div>
            <button className="cl-restore" style={restoreStyle} onClick={() => onRestore(d)}>
              <UndoIcon size={11} />Restore
            </button>
          </div>
        ))
      )}

      <div style={popFootStyle}>
        {canManageJobs ? (
          <>
            <span style={popNoteStyle}>Emptying is permanent and can't be undone.</span>
            <button
              className="cl-empty"
              style={deletedRows.length === 0 ? { ...emptyBtnStyle, ...emptyBtnLockedStyle } : emptyBtnStyle}
              disabled={deletedRows.length === 0}
              onClick={onEmpty}
            >
              {confirmEmpty ? `Confirm — delete ${deletedRows.length}` : "Empty bin"}
            </button>
          </>
        ) : (
          <>
            <span style={popNoteStyle}>Only an admin can permanently empty the bin.</span>
            <button style={{ ...emptyBtnStyle, ...emptyBtnLockedStyle }} disabled title="Admins only">🔒 Empty bin</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Main component
═══════════════════════════════════════════════════════════════ */

export default function CaptureLog({
  job, editing, isCrew,
  canEditShootWork, canManageJobs,
  currentSessionId, currentUserLabel, onRestoreRow,
  updateJobFields,
  canStartWork = false,
}) {
  const { studioSettings, updateJob } = useData();
  const equipment = studioSettings?.equipment || {};
  const crewRoster = studioSettings?.crewRoster || [];
  const storageOpts = studioSettings?.storageOpts || [];

  // Crew + admin can edit shoot work. Crew are always editable (no toggle);
  // admins edit when their read/edit toggle is on. Mirrors JobPrepTab so admin
  // behaviour is unchanged (admin: canEdit === editing).
  const canEdit = canEditShootWork && (editing || isCrew);

  const allEquipNames = useMemo(
    () => Object.values(equipment).flat().map(e => e.name),
    [equipment]
  );

  const [deleteMode, setDeleteMode] = useState(false);
  // The rig capture whose "Process" sheet is open: { sn, title } or null.
  const [processing, setProcessing] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDeleteRowId, setConfirmDeleteRowId] = useState(null);

  // Recently Deleted bin state
  const [binOpen, setBinOpen] = useState(false);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [toast, setToast] = useState(null); // { kind: "single", row } | { kind: "bulk", count }
  const actionsRef = useRef(null);
  const toastTimerRef = useRef(null);

  const savePatch = useCallback((patch) => {
    if (updateJobFields) updateJobFields(patch);
    else updateJob(job.id, patch);
  }, [updateJobFields, updateJob, job.id]);
  const upd = (field, val) => savePatch({ [field]: val });
  const captureLog = job.captureLog || [];
  const deletedRows = job.captureLogDeleted || [];

  // Latest-captureLog ref for async callbacks (photo upload resolves after ~500ms;
  // other edits in flight must not be clobbered by a stale closure).
  const captureLogRef = useRef(captureLog);
  useEffect(() => { captureLogRef.current = captureLog; }, [captureLog]);

  // Clean up the auto-dismiss timer on unmount.
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  // Disarm the "Empty bin" confirmation whenever the bin contents change (e.g. a
  // restore or a fresh delete) so it can't fire against a stale count.
  useEffect(() => { setConfirmEmpty(false); }, [deletedRows.length]);

  // Close the bin popover on an outside click (mousedown so it beats other handlers).
  useEffect(() => {
    if (!binOpen) return;
    const onDoc = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setBinOpen(false);
        setConfirmEmpty(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [binOpen]);

  // equipList memoized — recompute only when the job's selected equipment or the studio roster changes.
  const equipList = useMemo(() => {
    const selEquip = Array.from(new Set(job.selectedEquipment || []));
    return (selEquip.length > 0 ? selEquip : allEquipNames).slice().sort();
  }, [job.selectedEquipment, allEquipNames]);

  // Personnel refactor deferred — operator field stays as name string. normalizeCrew accepts array OR comma-string for legacy data.
  const crewNames = useMemo(() => normalizeCrew(job.crew), [job.crew]);

  const logDone = useMemo(() => captureLog.filter(r => r.status === "captured").length, [captureLog]);
  const logPending = captureLog.length - logDone;
  const protectedRows = useMemo(() => captureLog.filter(isIngestCaptureRow), [captureLog]);
  const manualRows = useMemo(() => captureLog.filter(r => !isIngestCaptureRow(r)), [captureLog]);

  // ── Segment 3: handheld/equipment subjects (READ-ONLY) merged into the list ──
  // jobs/{jobId}/subjects is JOB-scoped (one set per job, not per session).
  // Session-scoped tab jobs carry rootJobId from scopeJobToSession. These rows are
  // display-only: they are NEVER added to captureLog and NEVER counted, so
  // logDone/logPending/total above are structurally unaffected.
  const handheldJobId = job?.rootJobId || job?.id;
  const { data: handheldSubjects } = useFirestoreCollection(handheldJobId ? `jobs/${handheldJobId}/subjects` : null);
  const handheldRows = useMemo(
    () => [...handheldSubjects].sort((a, b) => (a.number ?? 0) - (b.number ?? 0)),
    [handheldSubjects],
  );

  const updateLog = (id, field, val) => upd("captureLog", captureLog.map(r => {
    if (r.id !== id) return r;
    // Subject cell mirror-write (v4 transition): keep displayName and subject in
    // lockstep so legacy readers and displayName-first readers see the same value.
    if (field === "subject" || field === "displayName") {
      return { ...r, subject: val, displayName: val, displayNameUpdatedAt: Date.now() };
    }
    return { ...r, [field]: val };
  }));
  const addLogRow = () => {
    const eq = {};
    equipList.forEach(name => { eq[name] = false; });
    upd("captureLog", [...captureLog, {
      id: `log_${mkUuid()}`,
      subject: "", displayName: "",
      source: "legacy_manual",
      subjectType: job.ingestSettings?.subjectType ?? "other",
      equipment: eq, storage: "",
      status: CAPTURE_LOG_STATUS_DEFAULT, operator: "", notes: "", images: [],
    }]);
  };

  // ── Soft delete: move rows OUT of the live log INTO the job-level bin ──
  const makeStamp = useCallback((row) => stampDeleted(row, {
    sessionId: currentSessionId || null,
    at: Date.now(),
    by: currentUserLabel || (isCrew ? "Crew" : "Admin"),
  }), [currentSessionId, currentUserLabel, isCrew]);

  const flashToast = useCallback((next) => {
    setToast(next);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  }, []);

  // Bulk clear: manual rows move to the bin; rig/ingest "protected" rows stay in
  // the live log (preserves the prior clearLogRows protection).
  const clearLogRows = () => {
    const stamped = manualRows.map(makeStamp);
    savePatch({
      captureLog: protectedRows,
      captureLogDeleted: [...stamped, ...deletedRows],
    });
    setConfirmClear(false);
    setConfirmDeleteRowId(null);
    setDeleteMode(false);
    if (stamped.length > 0) flashToast({ kind: "bulk", count: stamped.length });
  };

  const softDeleteRow = (row) => {
    const stamped = makeStamp(row);
    savePatch({
      captureLog: captureLog.filter(r => r.id !== row.id),
      captureLogDeleted: [stamped, ...deletedRows],
    });
    setConfirmDeleteRowId(null);
    flashToast({ kind: "single", row: stamped });
  };

  const removeLogRow = (row) => {
    // Protected (rig/ingest) rows still require an explicit confirm before they
    // leave the live log. The action is now a RECOVERABLE soft-delete.
    if (isIngestCaptureRow(row) && confirmDeleteRowId !== row.id) {
      setConfirmDeleteRowId(row.id);
      setConfirmClear(false);
      return;
    }
    softDeleteRow(row);
  };

  // Restore routes back to the row's origin day → handled by the parent
  // (cross-session aware). Empty is a root-only write → done locally.
  const handleRestore = (row) => {
    if (onRestoreRow) onRestoreRow(row);
    setToast(null);
    if (deletedRows.length <= 1) setBinOpen(false);
  };
  const handleEmpty = () => {
    if (!canManageJobs || deletedRows.length === 0) return;
    if (!confirmEmpty) { setConfirmEmpty(true); return; }
    savePatch({ captureLogDeleted: [] });
    setConfirmEmpty(false);
    setBinOpen(false);
  };

  const applyAll = (field, val) => upd("captureLog", captureLog.map(r => ({ ...r, [field]: val })));
  const setRowPhoto = useCallback((rowId, img) => {
    savePatch({
      captureLog: captureLogRef.current.map(r => r.id === rowId ? { ...r, images: [img] } : r),
    });
  }, [savePatch]);
  const removeRowPhoto = useCallback((rowId) => {
    savePatch({
      captureLog: captureLogRef.current.map(r => r.id === rowId ? { ...r, images: [] } : r),
    });
  }, [savePatch]);

  // The bin entry-point is deliberately low-key: only shown when there's
  // something in the bin OR you're mid-delete ("hide until needed").
  const showBinBtn = canEdit && (deletedRows.length > 0 || deleteMode);
  const nowTs = Date.now();

  const peoplePresent = job.peoplePresent || [];
  const clientContacts = job.clientContacts || [];

  return (
    // Guests' photos and names: a grey box in Feedback tab pictures (src/lib/feedback/screenshot.js).
    <div data-feedback-private="capture log">
      {/* Hover/focus styles for inline-styled inputs/selects */}
      <style>{`
        .cl-inp:hover, .cl-sel:hover { background: ${C.surfaceAlt} !important; }
        .cl-inp:focus, .cl-sel:focus { background: ${C.surface} !important; border-color: ${C.blue} !important; }
        .cl-row:hover .cl-cell { background: rgba(237,235,232,0.45); }
        .cl-action { opacity: 0; transition: opacity 0.15s; }
        .cl-row:hover .cl-action { opacity: 1; }
        .cl-status-sel { width: auto; }
        .cl-binbtn:hover { color: ${C.text} !important; border-style: solid !important; }
        .cl-restore:hover { border-color: ${C.green} !important; color: ${C.green} !important; }
        .cl-empty:not(:disabled):hover { background: ${C.danger} !important; color: #fff !important; }
        .cl-equip-opt:hover { background: ${C.surfaceAlt}; }
      `}</style>

      {/* ═══ Header ═══ */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ ...lbl, marginBottom: 0 }}>Capture Log</label>
          <span style={{
            fontSize: 10,
            color: logDone === captureLog.length && captureLog.length > 0 ? C.blue : C.muted,
          }}>
            {logDone} of {captureLog.length} captured
          </span>
        </div>
        {canEdit && (
          <div ref={actionsRef} style={{ display: "flex", gap: 6, alignItems: "center", position: "relative" }}>
            <button
              onClick={() => { setDeleteMode(!deleteMode); setConfirmClear(false); setConfirmDeleteRowId(null); }}
              style={{
                ...btnS,
                padding: "5px 12px", fontSize: 10,
                display: "flex", alignItems: "center", gap: 4,
                color: deleteMode ? C.danger : C.muted,
                borderColor: deleteMode ? "rgba(239,68,68,0.4)" : C.border,
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
              {deleteMode ? "Done" : "Delete"}
            </button>

            {/* Low-key contextual bin button, grouped next to Delete */}
            {showBinBtn && (
              <button
                className="cl-binbtn"
                onClick={(e) => { e.stopPropagation(); setBinOpen(o => !o); setConfirmEmpty(false); }}
                style={binBtnStyle}
                title="Recently Deleted"
              >
                <UndoIcon size={12} />
                Recently Deleted
                {deletedRows.length > 0 && <span style={binCountStyle}>{deletedRows.length}</span>}
              </button>
            )}

            {deleteMode && captureLog.length > 0 && (
              confirmClear ? (
                <button
                  onClick={clearLogRows}
                  style={{
                    ...btnS,
                    padding: "5px 12px", fontSize: 10,
                    display: "flex", alignItems: "center", gap: 4,
                    color: "#fff", background: C.danger, borderColor: C.danger,
                  }}
                >
                  {protectedRows.length > 0 ? `Confirm Clear Manual (${manualRows.length})` : `Confirm Clear All (${captureLog.length})`}
                </button>
              ) : (
                <button
                  onClick={() => { setConfirmClear(true); setConfirmDeleteRowId(null); }}
                  style={{
                    ...btnS,
                    padding: "5px 12px", fontSize: 10,
                    display: "flex", alignItems: "center", gap: 4,
                    color: C.danger, borderColor: "rgba(239,68,68,0.4)",
                    opacity: manualRows.length === 0 && protectedRows.length > 0 ? 0.45 : 1,
                  }}
                  disabled={manualRows.length === 0 && protectedRows.length > 0}
                  title={manualRows.length === 0 && protectedRows.length > 0 ? "Only ingest rows are present; delete them one at a time." : undefined}
                >
                  {protectedRows.length > 0 ? "Clear Manual" : "Clear All"}
                </button>
              )
            )}
            <button
              onClick={addLogRow}
              style={{ ...btnS, padding: "5px 12px", fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}
            >
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M8 3v10M3 8h10" />
              </svg>
              Add Row
            </button>

            {/* Recently Deleted popover */}
            {showBinBtn && binOpen && (
              <RecentlyDeletedPopover
                deletedRows={deletedRows}
                canManageJobs={canManageJobs}
                confirmEmpty={confirmEmpty}
                onRestore={handleRestore}
                onEmpty={handleEmpty}
                onClose={() => { setBinOpen(false); setConfirmEmpty(false); }}
                nowTs={nowTs}
              />
            )}

            {/* Undo toast after a delete */}
            {toast && (
              <div style={toastStyle}>
                {toast.kind === "bulk" ? (
                  <>
                    Moved {toast.count} to Recently Deleted
                    <button
                      style={toastLinkStyle}
                      onClick={(e) => { e.stopPropagation(); setToast(null); setBinOpen(true); }}
                    >Show</button>
                  </>
                ) : (
                  <>
                    Moved to Recently Deleted
                    <button
                      style={toastLinkStyle}
                      onClick={(e) => { e.stopPropagation(); handleRestore(toast.row); }}
                    >Undo</button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ Spreadsheet ═══ */}
      {(captureLog.length > 0 || handheldRows.length > 0) && (
        <div className="cl-gridwrap" style={{
          borderTop: `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
          overflowX: "auto",
        }}>
          <div className="cl-grid" style={{
            display: "grid",
            gridTemplateColumns: canEdit ? GRID_COLS_EDIT : GRID_COLS_READ,
            minWidth: "fit-content",
            alignItems: "stretch",
          }}>
            {/* Header row */}
            <div className="cl-head" style={{ display: "contents" }}>
              <div style={headerCellStyle}>#</div>
              <div style={headerCellStyle}></div>
              <div style={headerCellStyle}>Subject</div>
              <div style={headerCellStyle}>Equipment</div>
              <HeaderWithApplyAll
                label="Storage"
                options={storageOpts}
                applyLinkStyle={applyLinkStyle}
                headerCellStyle={headerCellStyle}
                onApply={(v) => applyAll("storage", v)}
                editing={canEdit}
              />
              <div style={headerCellStyle}>Status</div>
              <HeaderWithApplyAll
                label="Operator"
                options={crewNames}
                applyLinkStyle={applyLinkStyle}
                headerCellStyle={headerCellStyle}
                onApply={(v) => applyAll("operator", v)}
                editing={canEdit}
              />
              <div style={headerCellStyle}>Notes</div>
              {canEdit && <div style={headerCellStyle}></div>}
            </div>

            {/* Data rows */}
            {captureLog.map((row, idx) => {
              // Keep the last rig row's divider when handheld rows follow below.
              const isLast = idx === captureLog.length - 1 && handheldRows.length === 0;
              const photo = (row.images && row.images.length > 0) ? row.images[0] : null;
              const cellStyle = isLast ? lastRowCellStyle : rowCellStyle;
              const isProtected = isIngestCaptureRow(row);
              const confirmingProtectedDelete = confirmDeleteRowId === row.id;

              return (
                <div key={row.id} className="cl-row" style={{ display: "contents" }}>
                  {/* # */}
                  <div className="cl-cell" data-label="#" style={cellStyle}>
                    <span style={{ fontFamily: "'DM Mono', 'SF Mono', monospace", fontSize: 11, color: C.muted, opacity: 0.6 }}>
                      {idx + 1}
                    </span>
                  </div>

                  {/* Photo */}
                  <div className="cl-cell" data-label="Photo" style={cellStyle}>
                    <PhotoCell
                      image={photo}
                      onSet={(img) => setRowPhoto(row.id, img)}
                      editing={canEdit}
                    />
                  </div>

                  {/* Subject */}
                  <div className="cl-cell" data-label="Subject" style={cellStyle}>
                    {canEdit ? (
                      <input
                        key={row.id}
                        className="cl-inp"
                        aria-label="Subject"
                        defaultValue={subjectDisplay(row)}
                        onBlur={(e) => updateLog(row.id, "subject", e.target.value)}
                        placeholder="Subject..."
                        style={cellInpStyle}
                      />
                    ) : (
                      subjectDisplay(row) ? (
                        <span style={cellTextStyle}>{subjectDisplay(row)}</span>
                      ) : (
                        <span style={cellEmptyStyle}>No subject</span>
                      )
                    )}
                  </div>

                  {/* Equipment — checklist limited to this job's selected gear */}
                  <div className="cl-cell" data-label="Equipment" style={cellStyle}>
                    <EquipmentCell
                      equipment={row.equipment}
                      options={equipList}
                      editing={canEdit}
                      onChange={(map) => updateLog(row.id, "equipment", map)}
                    />
                  </div>

                  {/* Storage */}
                  <div className="cl-cell" data-label="Storage" style={cellStyle}>
                    {canEdit ? (
                      <select
                        className="cl-sel"
                        aria-label="Storage"
                        value={row.storage || ""}
                        onChange={(e) => updateLog(row.id, "storage", e.target.value)}
                        style={{ ...cellSelectStyle, fontFamily: "'DM Mono', 'SF Mono', monospace", fontSize: 12 }}
                      >
                        <option value="">—</option>
                        {storageOpts.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span style={row.storage ? cellMonoStyle : cellEmptyStyle}>{row.storage || "—"}</span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="cl-cell" data-label="Status" style={cellStyle}>
                    <StatusCell
                      status={row.status}
                      editing={canEdit}
                      onChange={(v) => updateLog(row.id, "status", v)}
                      testId={`capture-row-status-${row.id}`}
                    />
                    {canStartWork && processableSn(row) && (
                      <button
                        type="button"
                        className="cl-process"
                        data-testid={`capture-row-process-${row.id}`}
                        onClick={() => setProcessing({ sn: processableSn(row), title: subjectDisplay(row) })}
                        style={processBtnStyle}
                      >
                        Process
                      </button>
                    )}
                  </div>

                  {/* Operator */}
                  <div className="cl-cell" data-label="Operator" style={cellStyle}>
                    {canEdit ? (
                      <select
                        className="cl-sel"
                        aria-label="Operator"
                        value={row.operator || ""}
                        onChange={(e) => updateLog(row.id, "operator", e.target.value)}
                        style={cellSelectStyle}
                      >
                        <option value="">—</option>
                        {crewNames.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    ) : (
                      <span style={row.operator ? cellTextStyle : cellEmptyStyle}>{row.operator || "no operator"}</span>
                    )}
                  </div>

                  {/* Notes */}
                  <div className="cl-cell" data-label="Notes" style={cellStyle}>
                    {canEdit ? (
                      <input
                        key={row.id}
                        className="cl-inp"
                        aria-label="Notes"
                        defaultValue={row.notes || ""}
                        onBlur={(e) => updateLog(row.id, "notes", e.target.value)}
                        placeholder="Notes..."
                        style={cellInpStyle}
                      />
                    ) : (
                      <span style={row.notes ? cellTextStyle : cellEmptyStyle}>{row.notes || "No notes"}</span>
                    )}
                  </div>

                  {/* Action — destructive actions gated. Delete-mode: row delete (soft).
                       Normal edit mode: photo delete (only when a photo exists). */}
                  {canEdit && (
                    <div className="cl-cell cl-action" style={{ ...cellStyle, justifyContent: "flex-end", opacity: confirmingProtectedDelete ? 1 : undefined }}>
                      {deleteMode ? (
                        confirmingProtectedDelete ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <button
                              onClick={() => removeLogRow(row)}
                              title="Confirm — move this ingest capture row to Recently Deleted"
                              style={protectedDeleteConfirmStyle}
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDeleteRowId(null)}
                              title="Cancel"
                              style={protectedDeleteCancelStyle}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => removeLogRow(row)}
                            title={isProtected ? "Move ingest capture row to Recently Deleted" : "Move to Recently Deleted"}
                            style={{ background: "none", border: "none", cursor: "pointer", color: isProtected ? C.amber : C.danger, padding: 2 }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                          </button>
                        )
                      ) : photo ? (
                        <button
                          onClick={() => removeRowPhoto(row.id)}
                          title="Delete photo"
                          style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, opacity: 0.6, padding: 2 }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          </svg>
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Segment 3 — read-only handheld/equipment rows in the same list */}
            {handheldRows.length > 0 && (
              <HandheldCaptureRows subjects={handheldRows} canEdit={canEdit} />
            )}
          </div>
        </div>
      )}

      {/* Footer (counts) — rig counts are computed from captureLog ONLY and are
           unchanged by handheld rows; the equipment count is shown separately and
           is never folded into the rig totals. */}
      {(captureLog.length > 0 || handheldRows.length > 0) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
          <div style={{ display: "flex", gap: 16, fontSize: 11 }}>
            {logDone > 0 && <span style={{ color: C.blue }}>{logDone} captured</span>}
            {logPending > 0 && <span style={{ color: C.muted }}>{logPending} pending</span>}
            {handheldRows.length > 0 && <span style={{ color: HH_ACCENT }}>{handheldRows.length} equipment</span>}
          </div>
          {captureLog.length > 0 && (
            <span style={{ fontSize: 11, color: C.muted, fontFamily: "'DM Mono', 'SF Mono', monospace" }}>
              {captureLog.length} scans total
            </span>
          )}
        </div>
      )}

      {captureLog.length === 0 && handheldRows.length === 0 && (
        <div style={{
          fontSize: 12, color: C.muted, opacity: 0.6, textAlign: "center", padding: 20,
          fontFamily: font, fontWeight: 300,
          borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
        }}>
          {canEdit ? "No capture log entries — use Add Row above to add entries" : "No capture log entries yet"}
        </div>
      )}

      {/* ═══ Actual Times ═══ */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18, marginTop: 22 }}>
        <div className="cl-times" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div>
            <label style={lbl}>Actual Arrival</label>
            {canEdit
              ? <input type="time" value={job.actualArrival || ""} onChange={(e) => upd("actualArrival", e.target.value)} style={inp} />
              : <ReadValue value={job.actualArrival} />}
          </div>
          <div>
            <label style={lbl}>Actual Wrap</label>
            {canEdit
              ? <input type="time" value={job.actualWrap || ""} onChange={(e) => upd("actualWrap", e.target.value)} style={inp} />
              : <ReadValue value={job.actualWrap} />}
          </div>
          <div>
            <label style={lbl}>Travel Time (hrs)</label>
            {canEdit
              ? <input type="number" value={job.travelTime ?? ""} onChange={(e) => upd("travelTime", e.target.value)} style={inp} />
              : <ReadValue value={job.travelTime} />}
          </div>
        </div>
      </div>

      {/* ═══ Client Contacts ═══ */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18, marginTop: 22 }}>
        <ContactTable
          items={clientContacts}
          onUpdate={(v) => upd("clientContacts", v)}
          label="Client Contacts"
          addLabel="Add Contact"
          editing={canEdit}
          fields={[
            { key: "name", placeholder: "Name", flex: 1.2 },
            { key: "role", placeholder: "Role", flex: 0.8 },
            { key: "phone", placeholder: "Phone", flex: 1 },
            { key: "email", placeholder: "Email", flex: 1.2 },
          ]}
        />
      </div>

      {/* ═══ People Present ═══ */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18, marginTop: 22 }}>
        <ContactTable
          items={peoplePresent}
          onUpdate={(v) => upd("peoplePresent", v)}
          label="People Present"
          addLabel="Add Person"
          editing={canEdit}
          fields={[
            { key: "name", placeholder: "Name", flex: 1.5 },
            { key: "role", placeholder: "Role", flex: 1 },
          ]}
        />
      </div>

      {/* ═══ Notes + Issues ═══ */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18, marginTop: 22 }}>
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Capture Notes</label>
          {canEdit
            ? <textarea rows={2} value={job.captureNotes || ""} onChange={(e) => upd("captureNotes", e.target.value)} placeholder="How did the capture go..." style={{ ...inp, resize: "vertical" }} />
            : <ReadValue value={job.captureNotes} padding="2px 0" />}
        </div>
        <div>
          <label style={lbl}>Issues</label>
          {canEdit
            ? <input value={job.issues || ""} onChange={(e) => upd("issues", e.target.value)} placeholder="Any problems..." style={inp} />
            : <ReadValue value={job.issues} padding="2px 0" />}
        </div>
      </div>

      {/* Slice 3.2: the Process sheet for one rig capture (the job is the root job, not a session's view). */}
      {processing && (
        <ProcessCaptureSheet
          jobId={job.rootJobId || job.id}
          sn={processing.sn}
          title={processing.title}
          onClose={() => setProcessing(null)}
        />
      )}
    </div>
  );
}
